import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Check, Info, Search, Star, X } from 'lucide-react';
import { initials, phoneWhileTyping } from '../lib/format.js';

export const cx = (...parts) => parts.filter(Boolean).join(' ');

/* -------------------------------------------------------------- buttons */

export function Button({
  children,
  variant = 'default',
  size = 'md',
  icon: Icon,
  trailing,
  loading = false,
  className,
  ...rest
}) {
  return (
    <button
      type="button"
      className={cx(
        'btn',
        variant !== 'default' && `btn-${variant}`,
        size !== 'md' && `btn-${size}`,
        className,
      )}
      aria-disabled={rest.disabled || loading}
      {...rest}
    >
      {loading ? <span className="spinner" /> : Icon ? <Icon size={size === 'sm' ? 13 : 15} strokeWidth={2.1} /> : null}
      {children}
      {trailing}
    </button>
  );
}

export function IconButton({ icon: Icon, label, size = 'md', className, ...rest }) {
  return (
    <button
      type="button"
      className={cx('btn', 'btn-ghost', 'btn-icon', size === 'sm' && 'sm', className)}
      title={label}
      aria-label={label}
      {...rest}
    >
      <Icon size={size === 'sm' ? 14 : 16} strokeWidth={2.1} />
    </button>
  );
}

/* ---------------------------------------------------------------- cards */

export function Card({ children, className, tone, hot, flat, ...rest }) {
  return (
    <div
      className={cx('card', hot && 'hot', flat && 'flat', className)}
      data-tone={tone}
      {...rest}
    >
      {children}
    </div>
  );
}

export function CardHead({ title, subtitle, icon: Icon, actions, children, className }) {
  return (
    <div className={cx('card-head', className)}>
      {Icon ? (
        <span className="empty-icon" style={{ width: 32, height: 32, borderRadius: 9 }}>
          <Icon size={15} strokeWidth={2} />
        </span>
      ) : null}
      <div className="grow">
        {title ? <h4 className="truncate">{title}</h4> : null}
        {subtitle ? <div className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{subtitle}</div> : null}
        {children}
      </div>
      {actions ? <div className="card-head-actions">{actions}</div> : null}
    </div>
  );
}

export const CardBody = ({ children, className, ...rest }) => (
  <div className={cx('card-body', className)} {...rest}>
    {children}
  </div>
);

export const CardFoot = ({ children, className, ...rest }) => (
  <div className={cx('card-foot', className)} {...rest}>
    {children}
  </div>
);

/* ------------------------------------------------------------------ stat */

export function Stat({ label, value, unit, tone = 'amber', icon: Icon, delta, foot, spark, size, hint }) {
  return (
    <div className="stat" data-tone={tone}>
      <div className="stat-label">
        {Icon ? <Icon size={13} strokeWidth={2.2} style={{ color: 'var(--tone)' }} /> : null}
        <span className="label">{label}</span>
      </div>
      <div className={cx('stat-value', size === 'sm' && 'sm')}>
        {value}
        {unit ? <span className="unit">{unit}</span> : null}
      </div>
      {(delta !== undefined && delta !== null) || foot || spark ? (
        <div className="stat-foot">
          {delta !== undefined && delta !== null ? <Delta value={delta} /> : null}
          {foot ? <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{foot}</span> : null}
          {spark ? <span style={{ marginLeft: 'auto' }}>{spark}</span> : null}
        </div>
      ) : null}
      {hint ? <div className="dimmer mt-8" style={{ fontSize: 'var(--fs-xs)' }}>{hint}</div> : null}
    </div>
  );
}

export function Delta({ value, suffix = '%' }) {
  const n = Number(value || 0);
  const dir = n > 0.05 ? 'up' : n < -0.05 ? 'down' : 'flat';
  const arrow = dir === 'up' ? '▲' : dir === 'down' ? '▼' : '■';
  return (
    <span className={cx('delta', dir)}>
      <span style={{ fontSize: 8 }}>{arrow}</span>
      {Math.abs(n).toFixed(Math.abs(n) < 10 ? 1 : 0)}
      {suffix}
    </span>
  );
}

/* --------------------------------------------------------- badges/pills */

export const Badge = ({ children, tone = 'muted', solid, size, dot, live, className, style, ...rest }) => (
  <span
    className={cx('badge', solid && 'solid', size === 'sm' && 'sm', size === 'lg' && 'lg', live && 'live', className)}
    data-tone={tone}
    style={style}
    {...rest}
  >
    {dot ? <span className="dot" /> : null}
    {children}
  </span>
);

export const Chip = ({ children, tone, active, onClick, onRemove, icon: Icon, className }) => (
  <span
    className={cx('chip', active && 'active', className)}
    data-tone={tone}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    style={onClick ? { cursor: 'pointer' } : undefined}
  >
    {Icon ? <Icon size={12} strokeWidth={2.2} /> : null}
    {children}
    {onRemove ? (
      <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(); }} aria-label="Remove">
        <X size={11} strokeWidth={2.6} />
      </button>
    ) : null}
  </span>
);

/* ---------------------------------------------------------------- inputs */

export const Field = ({ label, hint, error, children, className, required }) => (
  <label className={cx('field', className)}>
    {label ? (
      <span className="label">
        {label}
        {required ? <span style={{ color: 'var(--amber)' }}> *</span> : null}
      </span>
    ) : null}
    {children}
    {hint && !error ? <span className="field-hint">{hint}</span> : null}
    {error ? <span className="field-error">{error}</span> : null}
  </label>
);

export const Input = ({ className, ...rest }) => <input className={cx('input', className)} {...rest} />;

/**
 * A phone number field that writes the number the way a person would.
 *
 * It formats as you type, and gets out of the way the moment you type something
 * it does not recognise — a plus, a letter, an extension.
 */
export const PhoneInput = ({ value, onChange, className, ...rest }) => (
  <input
    className={cx('input', className)}
    type="tel"
    inputMode="tel"
    autoComplete="tel"
    value={value ?? ''}
    onChange={(event) => onChange(phoneWhileTyping(event.target.value))}
    {...rest}
  />
);

export const Textarea = ({ className, ...rest }) => <textarea className={cx('textarea', className)} {...rest} />;

export function Select({ children, options, className, placeholder, ...rest }) {
  return (
    <select className={cx('select', className)} {...rest}>
      {placeholder ? <option value="">{placeholder}</option> : null}
      {options
        ? options.map((opt) => (
            <option key={opt.value ?? opt} value={opt.value ?? opt}>
              {opt.label ?? opt}
            </option>
          ))
        : children}
    </select>
  );
}

export const Checkbox = ({ label, checked, onChange, name, value, type = 'checkbox', className }) => (
  <label className={cx('check', type === 'radio' && 'radio', className)}>
    <input type={type} name={name} value={value} checked={checked} onChange={onChange} />
    {label ? <span>{label}</span> : null}
  </label>
);

export const Switch = ({ label, checked, onChange, className, ...rest }) => (
  <label className={cx('switch', className)}>
    <input type="checkbox" checked={checked} onChange={onChange} {...rest} />
    {label ? <span>{label}</span> : null}
  </label>
);

export const SearchInput = ({ value, onChange, placeholder = 'Search…', className, ...rest }) => (
  <div className={cx('search', className)}>
    <Search size={14} strokeWidth={2.2} />
    <input
      className="input"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      {...rest}
    />
  </div>
);

export function Segmented({ options, value, onChange, className, amber = true }) {
  return (
    <div className={cx('segmented', className)}>
      {options.map((opt) => (
        <button
          key={opt.value ?? opt}
          type="button"
          className={cx(value === (opt.value ?? opt) && 'active', value === (opt.value ?? opt) && amber && 'amber')}
          onClick={() => onChange(opt.value ?? opt)}
        >
          {opt.icon ? <opt.icon size={13} strokeWidth={2.2} /> : null}
          {opt.label ?? opt}
        </button>
      ))}
    </div>
  );
}

export function Tabs({ tabs, value, onChange, className }) {
  return (
    <div className={cx('tabs', className)}>
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          className={cx('tab', value === tab.value && 'active')}
          onClick={() => onChange(tab.value)}
        >
          {tab.icon ? <tab.icon size={14} strokeWidth={2.1} /> : null}
          {tab.label}
          {tab.count !== undefined && tab.count !== null ? <span className="nav-count">{tab.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------- people */

export const Avatar = ({ name, tone = 'amber', size = 'md', className }) => (
  <span className={cx('avatar', size !== 'md' && size, className)} data-tone={tone}>
    {initials(name)}
  </span>
);

export function Stars({ value = 0, size = 12, showValue = true }) {
  const rounded = Math.round(Number(value) * 2) / 2;
  return (
    <span className="row gap-6">
      <span className="stars">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star key={i} size={size} strokeWidth={1.6} style={{ opacity: i <= rounded ? 1 : 0.22, fill: i <= rounded ? 'currentColor' : 'none' }} />
        ))}
      </span>
      {showValue ? <span className="num" style={{ fontSize: 'var(--fs-sm)', color: 'var(--fg-2)' }}>{Number(value || 0).toFixed(1)}</span> : null}
    </span>
  );
}

export const ScoreRing = ({ value = 0, max = 100, tone = 'amber', size = 54, label }) => {
  const radius = (size - 6) / 2;
  const circumference = 2 * Math.PI * radius;
  const ratio = Math.max(0, Math.min(1, Number(value) / max));
  return (
    <span className="score-ring" style={{ width: size, height: size }} data-tone={tone}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,.1)" strokeWidth="4" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--tone)"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={`${circumference * ratio} ${circumference}`}
          style={{ transition: 'stroke-dasharray 500ms var(--ease)' }}
        />
      </svg>
      <span>{label ?? Math.round(value)}</span>
    </span>
  );
};

/* -------------------------------------------------------------- progress */

export const Progress = ({ value = 0, tone, striped, thin, className }) => (
  <div className={cx('progress', tone, striped && 'striped', thin && 'thin', className)}>
    <span style={{ width: `${Math.max(0, Math.min(100, Number(value)))}%` }} />
  </div>
);

export function MeterRow({ label, value, max, display, tone = 'amber' }) {
  const ratio = max ? (Number(value) / Number(max)) * 100 : 0;
  return (
    <div className="meter-row">
      <div className="grow">
        <div className="row between mb-4">
          <span className="truncate" style={{ fontSize: 'var(--fs-sm)' }}>{label}</span>
        </div>
        <Progress value={ratio} tone={tone} thin />
      </div>
      <span className="num" style={{ fontSize: 'var(--fs-sm)', color: 'var(--fg-2)' }}>{display}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ empty */

export const EmptyState = ({ icon: Icon = Info, title, children, action }) => (
  <div className="empty">
    <span className="empty-icon">
      <Icon size={20} strokeWidth={1.9} />
    </span>
    <h4>{title}</h4>
    {children ? <p>{children}</p> : null}
    {action}
  </div>
);

export const Loading = ({ rows = 4, className }) => (
  <div className={cx('col gap-12', className)} style={{ padding: 16 }}>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="skeleton" style={{ height: 16, width: `${90 - i * 7}%` }} />
    ))}
  </div>
);

export const Callout = ({ tone = 'amber', icon: Icon = AlertTriangle, title, children, className, actions }) => (
  <div
    className={cx('row-top', 'gap-12', className)}
    data-tone={tone}
    style={{
      padding: '12px 14px',
      borderRadius: 'var(--r-md)',
      background: 'var(--tone-soft)',
      border: '1px solid var(--tone-line)',
    }}
  >
    <Icon size={16} strokeWidth={2.1} style={{ color: 'var(--tone)', marginTop: 2, flex: '0 0 auto' }} />
    <div className="grow">
      {title ? <strong style={{ fontSize: 'var(--fs-md)', color: 'var(--fg)' }}>{title}</strong> : null}
      {children ? <div className="muted" style={{ fontSize: 'var(--fs-sm)', marginTop: 3 }}>{children}</div> : null}
      {actions ? <div className="row gap-8 mt-12">{actions}</div> : null}
    </div>
  </div>
);

/* --------------------------------------------------------------- overlay */

function Portal({ children }) {
  return createPortal(children, document.body);
}

export function Modal({ open, onClose, title, subtitle, children, footer, size = 'md', tone }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <Portal>
      <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
        <div className={cx('modal', size !== 'md' && size)} data-tone={tone} role="dialog" aria-modal="true">
          <div className="modal-head">
            <div className="grow">
              <h3>{title}</h3>
              {subtitle ? <div className="muted mt-4" style={{ fontSize: 'var(--fs-sm)' }}>{subtitle}</div> : null}
            </div>
            <IconButton icon={X} label="Close" onClick={onClose} />
          </div>
          <div className="modal-body">{children}</div>
          {footer ? <div className="modal-foot">{footer}</div> : null}
        </div>
      </div>
    </Portal>
  );
}

export function Drawer({ open, onClose, title, subtitle, children, footer, size = 'md', actions }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <Portal>
      <div className="modal-backdrop" style={{ padding: 0, alignItems: 'stretch', justifyContent: 'flex-end' }} onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
        <div className={cx('drawer', size !== 'md' && size)} role="dialog" aria-modal="true">
          <div className="drawer-head">
            <div className="grow">
              <h4>{title}</h4>
              {subtitle ? <div className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{subtitle}</div> : null}
            </div>
            {actions}
            <IconButton icon={X} label="Close" onClick={onClose} />
          </div>
          <div className="drawer-body">{children}</div>
          {footer ? <div className="drawer-foot">{footer}</div> : null}
        </div>
      </div>
    </Portal>
  );
}

export function ConfirmDialog({ open, onClose, onConfirm, title, body, confirmLabel = 'Confirm', danger, loading }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>{confirmLabel}</Button>
        </>
      }
    >
      <p className="muted">{body}</p>
    </Modal>
  );
}

/* ----------------------------------------------------------------- toast */

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id) => setToasts((list) => list.filter((t) => t.id !== id)), []);

  const push = useCallback(
    (toast) => {
      const id = (idRef.current += 1);
      const entry = typeof toast === 'string' ? { title: toast } : toast;
      setToasts((list) => [...list, { id, kind: 'ok', ...entry }]);
      setTimeout(() => dismiss(id), entry.duration ?? 5200);
      return id;
    },
    [dismiss],
  );

  const value = useMemo(
    () => ({
      push,
      ok: (title, body) => push({ title, body, kind: 'ok' }),
      err: (title, body) => push({ title, body, kind: 'err', duration: 7000 }),
      info: (title, body) => push({ title, body, kind: 'info' }),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Portal>
        <div className="toasts">
          {toasts.map((toast) => (
            <div key={toast.id} className={cx('toast', toast.kind)} onClick={() => dismiss(toast.id)}>
              <span style={{ color: 'var(--tone)', marginTop: 1 }}>
                {toast.kind === 'err' ? <AlertTriangle size={15} /> : toast.kind === 'info' ? <Info size={15} /> : <Check size={15} />}
              </span>
              <div className="grow">
                <strong>{toast.title}</strong>
                {toast.body ? <p>{toast.body}</p> : null}
              </div>
            </div>
          ))}
        </div>
      </Portal>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}

/* ------------------------------------------------------------- data bits */

export function KeyValue({ label, children, className, align = 'right' }) {
  return (
    <div className={cx('row between gap-16', className)} style={{ padding: '6px 0' }}>
      <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{label}</span>
      <span className="num" style={{ textAlign: align, fontSize: 'var(--fs-md)' }}>{children}</span>
    </div>
  );
}

export function Pagination({ total, limit, offset, onChange }) {
  const pages = Math.max(1, Math.ceil(total / limit));
  const page = Math.floor(offset / limit) + 1;
  if (total <= limit) return null;
  return (
    <div className="table-foot">
      <span>
        {offset + 1}–{Math.min(offset + limit, total)} of {total}
      </span>
      <div className="row gap-8">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onChange((page - 2) * limit)}>
          Previous
        </Button>
        <span className="dim">Page {page} / {pages}</span>
        <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => onChange(page * limit)}>
          Next
        </Button>
      </div>
    </div>
  );
}

export function Timeline({ items, renderItem }) {
  return (
    <div className="timeline">
      {items.map((item, i) => (
        <div key={item.id ?? i} className={cx('tl-item', item.strong && 'strong')} data-tone={item.tone}>
          {renderItem ? renderItem(item) : (
            <>
              <div className="tl-head">
                <strong style={{ fontSize: 'var(--fs-md)' }}>{item.title}</strong>
                {item.badge}
                <span className="dimmer" style={{ fontSize: 'var(--fs-xs)' }}>{item.when}</span>
              </div>
              {item.body ? <div className="tl-body">{item.body}</div> : null}
            </>
          )}
        </div>
      ))}
    </div>
  );
}

export const Toolbar = ({ children, className }) => <div className={cx('table-toolbar', className)}>{children}</div>;

export function StarRating({ value = 0, onChange, readOnly, size = 18 }) {
  return (
    <span className="stars">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          onClick={() => !readOnly && onChange?.(i)}
          style={{ background: 'none', border: 0, padding: 0, cursor: readOnly ? 'default' : 'pointer', color: 'inherit' }}
          aria-label={`${i} star`}
        >
          <Star size={size} strokeWidth={1.6} style={{ fill: i <= value ? 'currentColor' : 'none', opacity: i <= value ? 1 : 0.3 }} />
        </button>
      ))}
    </span>
  );
}
